import React from 'react';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { CartItem as CartItemType } from '../../lib/types';
import { formatCurrency, cn, aggregateQuantityOptions } from '../../lib/utils';
interface CartItemProps {
  item: CartItemType;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
  theme?: 'default' | 'capricho';
}
export function CartItem({
  item,
  onUpdateQuantity,
  onRemove,
  theme = 'default'
}: CartItemProps) {
  const optionsTotal = item.selected_options.reduce((sum, opt) => sum + opt.price_delta, 0);
  const unitPrice = item.unit_price_snapshot + optionsTotal;
  const lineTotal = unitPrice * item.quantity;
  const isCapricho = theme === 'capricho';
  return <div className={cn('flex gap-3 py-4 border-b last:border-0', isCapricho ? 'border-[#E8DDD0]' : 'border-gray-100')} data-id="element-2110">
      {/* Image if available */}
      {item.image_url && <div className={cn('w-14 h-14 rounded-xl overflow-hidden shrink-0 border', isCapricho ? 'bg-[#F5E9DA] border-[#E8DDD0]' : 'bg-gray-50 border-gray-200')} data-id="element-2111">
          <img src={item.image_url} alt={item.item_name_snapshot} className="w-full h-full object-cover" data-id="element-2112" />
        </div>}

      <div className="flex-1 flex flex-col min-w-0" data-id="element-2113">
        <div className="flex justify-between items-start mb-0.5" data-id="element-2114">
          <h4 className={cn('font-semibold text-sm leading-tight pr-2', isCapricho ? 'text-[#3D2B1F]' : 'text-gray-900')} data-id="element-2115">
            {item.item_name_snapshot}
          </h4>
          <button onClick={() => onRemove(item.id)} className={cn('transition-colors p-1 -mr-1 shrink-0', isCapricho ? 'text-[#8B7355] hover:text-[#CE1126]' : 'text-gray-400 hover:text-red-500')} aria-label="Eliminar producto" data-id="element-2116">
            <Trash2 className="w-5 h-5" data-id="element-2117" />
          </button>
        </div>

        {/* Options */}
        {item.selected_options.length > 0 && <ul className={cn('text-xs mb-1.5 space-y-0.5', isCapricho ? 'text-[#8B7355]' : 'text-gray-500')} data-id="element-2118">
            {aggregateQuantityOptions(item.selected_options).map((opt, idx) => <li key={idx} className="flex justify-between" data-id="element-2119">
                <span className="truncate pr-2" data-id="element-2120">
                  • {opt.option_name}
                  {opt.qty > 1 ? ` x${opt.qty}` : ''}
                </span>
                {opt.price_delta > 0 && <span className="shrink-0" data-id="element-2121">
                    +{formatCurrency(opt.price_delta)}
                  </span>}
              </li>)}
          </ul>}

        {/* Notes */}
        {item.notes && <p className={cn('text-xs px-2 py-1 rounded-md mb-2 italic line-clamp-2', isCapricho ? 'text-[#8B5E3C] bg-[#F5E9DA]' : 'text-amber-600 bg-amber-50')} data-id="element-2122">
            "{item.notes}"
          </p>}

        <div className="mt-auto flex items-center justify-between pt-1" data-id="element-2123">
          {/* Quantity Controls */}
          <div className={cn('flex items-center rounded-lg p-0.5 border', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-gray-100 border-gray-200')} data-id="element-2124">
            <button onClick={() => onUpdateQuantity(item.id, item.quantity - 1)} className={cn('w-8 h-8 flex items-center justify-center rounded-md transition-all', isCapricho ? 'text-[#8B7355] hover:text-[#3D2B1F] hover:bg-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200')} data-id="element-2125">
              <Minus className="w-4 h-4" data-id="element-2126" />
            </button>
            <span className={cn('w-8 text-center font-semibold text-sm', isCapricho ? 'text-[#3D2B1F]' : 'text-gray-900')} data-id="element-2127">
              {item.quantity}
            </span>
            <button onClick={() => onUpdateQuantity(item.id, item.quantity + 1)} className={cn('w-8 h-8 flex items-center justify-center rounded-md transition-all', isCapricho ? 'text-[#8B7355] hover:text-[#3D2B1F] hover:bg-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200')} data-id="element-2128">
              <Plus className="w-4 h-4" data-id="element-2129" />
            </button>
          </div>

          <div className={cn('font-bold text-sm', isCapricho ? 'text-[#C8A96A]' : 'text-amber-600')} data-id="element-2130">
            {formatCurrency(lineTotal)}
          </div>
        </div>
      </div>
    </div>;
}